import {
  GroupedSelectChangeMeta,
  GroupedSelectGroup,
  GroupedSelectGroupValue,
  GroupedSelectOption,
  GroupedSelectPerGroupProps,
  GroupedSelectProps,
  GroupedSelectSelectionMode,
  GroupedSelectSingleProps,
  GroupedSelectValue,
} from './GroupedSelect.interface';

export const GROUPED_SELECT_OTHER_KEY = '__other__';

export function getGroupKey(group: GroupedSelectGroup, index: number): string {
  return group.id || group.subheading || `group-${index}`;
}

export function isPerGroupSelection(
  groups: GroupedSelectGroup[],
  selectionMode?: 'single' | 'perGroup',
): boolean {
  if (selectionMode === 'perGroup') {
    return true;
  }

  if (selectionMode === 'single') {
    return false;
  }

  return groups.filter(group => group.subheading).length > 1;
}

export function isGroupValue(
  value: GroupedSelectValue | undefined,
): value is GroupedSelectGroupValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function flattenOptions(
  groups: GroupedSelectGroup[],
  otherOption?: GroupedSelectOption,
): GroupedSelectOption[] {
  const options = groups.flatMap(group => group.options);
  if (otherOption) {
    options.push(otherOption);
  }
  return options;
}

export function getSelectedDisplayLabel(
  value: GroupedSelectValue | undefined,
  groups: GroupedSelectGroup[],
  otherOption?: GroupedSelectOption,
): string | undefined {
  if (!value || (isGroupValue(value) && Object.keys(value).length === 0)) {
    return undefined;
  }

  if (isGroupValue(value)) {
    if (value[GROUPED_SELECT_OTHER_KEY] && otherOption) {
      return otherOption.label;
    }

    const labels = groups
      .map((group, index) => {
        const groupKey = getGroupKey(group, index);
        const selectedValue = value[groupKey];
        if (!selectedValue) {
          return null;
        }

        return (
          group.options.find(option => option.value === selectedValue)?.label ||
          null
        );
      })
      .filter(Boolean);

    return labels.length > 0 ? labels.join(', ') : undefined;
  }

  return flattenOptions(groups, otherOption).find(
    option => option.value === value,
  )?.label;
}

export function resolveSelectionMode(
  groups: GroupedSelectGroup[],
  selectionMode?: GroupedSelectSelectionMode,
): GroupedSelectSelectionMode {
  return selectionMode ?? (isPerGroupSelection(groups) ? 'perGroup' : 'single');
}

const notifySingleChange = (
  nextValue: string,
  meta: GroupedSelectChangeMeta | undefined,
  onChange?: GroupedSelectSingleProps['onChange'],
  onSelectChange?: GroupedSelectSingleProps['onSelectChange'],
) => {
  onChange?.(nextValue, meta);
  onSelectChange?.(nextValue);
};

const notifyPerGroupChange = (
  nextValue: GroupedSelectGroupValue,
  meta: GroupedSelectChangeMeta | undefined,
  onChange?: GroupedSelectPerGroupProps['onChange'],
  onSelectChange?: GroupedSelectPerGroupProps['onSelectChange'],
) => {
  onChange?.(nextValue, meta);
  onSelectChange?.(nextValue);
};

const changeHandlerByMode = {
  single:
    (
      onChange?: GroupedSelectProps['onChange'],
      onSelectChange?: GroupedSelectProps['onSelectChange'],
    ) =>
    (nextValue: GroupedSelectValue, meta?: GroupedSelectChangeMeta) => {
      notifySingleChange(
        nextValue as string,
        meta,
        onChange as GroupedSelectSingleProps['onChange'],
        onSelectChange as GroupedSelectSingleProps['onSelectChange'],
      );
    },
  perGroup:
    (
      onChange?: GroupedSelectProps['onChange'],
      onSelectChange?: GroupedSelectProps['onSelectChange'],
    ) =>
    (nextValue: GroupedSelectValue, meta?: GroupedSelectChangeMeta) => {
      notifyPerGroupChange(
        nextValue as GroupedSelectGroupValue,
        meta,
        onChange as GroupedSelectPerGroupProps['onChange'],
        onSelectChange as GroupedSelectPerGroupProps['onSelectChange'],
      );
    },
};

export function createChangeHandler(
  selectionMode: GroupedSelectSelectionMode,
  onChange?: GroupedSelectProps['onChange'],
  onSelectChange?: GroupedSelectProps['onSelectChange'],
) {
  return changeHandlerByMode[selectionMode](onChange, onSelectChange);
}
